import { useState } from 'react'
import { useHistory } from 'react-router-dom'

import Loading from '../../../templates/UI/Loading/Loading'
import Dollars from '../../../templates/Dollars/Dollars'
import useCallApi from '../../../hooks/useCallApi'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import DateRangeFilter from '../../Reports/Filters/DateRangeFilter/DateRangeFilter'

const AffiliateBonusReport = (props) => {
    const { showSystemMessage } = props
    const history = useHistory()

    document.title = 'Affiliate Performance Bonus Report'

    const {
        selectedStartDate,
        selectedEndDate,
        quickDate,
        handleChangeStart,
        handleChangeEnd,
        handleQuickDate,
    } = useDateRangeFilter('month')

    const [appliedStartDate, setAppliedStartDate] = useState(selectedStartDate)
    const [appliedEndDate, setAppliedEndDate] = useState(selectedEndDate)

    const apiUrl = `reports/custom/affiliateBonusReport?startDate=${appliedStartDate}&endDate=${appliedEndDate}`

    const { dataLoaded, data } = useCallApi(
        apiUrl,
        'GET',
        {},
        [apiUrl],
        showSystemMessage
    )

    const report = data && data.report ? data.report : []

    const getProgressBarClass = (percent) => {
        if (percent >= 100) return 'bg-info'  
        if (percent >= 50) return 'bg-primary'    
        return 'bg-danger'
    }

    const calculateRemainingDays = (endDateString) => {
        if (!endDateString) return null
        
        const endDate = new Date(endDateString)
        if (isNaN(endDate.getTime())) return null
        
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        endDate.setHours(0, 0, 0, 0)
        
        const diffTime = endDate - today
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays < 0) {
            return 0
        }
        
        return diffDays
    }

    const refreshReport = (e) => {
        e.preventDefault()

        setAppliedStartDate(selectedStartDate)
        setAppliedEndDate(selectedEndDate)

        const params = new URLSearchParams(window.location.search)
        params.set('startDate', selectedStartDate)
        params.set('endDate', selectedEndDate)
        history.push({ search: params.toString() })
    }

    const columns = [
        {
            Header: 'Affiliate',
            accessor: 'affiliate_name',
            Cell: (tableData) => {
                const row = tableData.row.original
                return <Affiliate
                    aid={row.affiliate_id}
                    name={row.affiliate_name}
                    priority={row.aff_priority}
                    status={row.status}
                    showSystemMessage={showSystemMessage}
                />
            },
        },
        {

            Header: 'Affiliate Manager',
            accessor: 'affiliateManagerName',
            Cell: (tableData) => {
                const row = tableData.row.original
                const managerName = row.affiliateManagerName
                return <>{managerName ? managerName : '—'}</>
            },
        },
        {
            Header: () => <div className="text-right">Earnings / Tier</div>,
            accessor: 'promotionEarnings',
            className: 'text-right',
            headerClassName: 'text-right',
            sortType: 'basic',
            Cell: (tableData) => {
                const row = tableData.row.original
                const earnings = row.promotionEarnings || 0
                const goal = row.tierEarningsGoal

                return (
                    <>
                        <Dollars value={earnings} /> / {goal ? <Dollars value={goal} /> : '-'}
                    </>
                )
            },
        },  
        {
            Header: () => <div className="text-center">Progress Towards Working Tier</div>,
            accessor: 'progressPercent',
            className: 'text-right',
            headerClassName: 'text-right',
            Cell: (tableData) => {
                const row = tableData.row.original
                const percent = row.progressPercent || 0
                const safePercent = Math.min(Math.max(percent, 0), 100)

                return (
                    <div className="d-flex align-items-center justify-content-end">
                        <div
                            className="progress flex-grow-1 mr-2 mt-1 rounded bg-dark"
                            style={{
                                height: '6px',
                                maxWidth: '200px',
                                position: 'relative',
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                            }}
                        >
                            <div
                                className={`progress-bar rounded ${getProgressBarClass(safePercent)}`}
                                role="progressbar"
                                style={{ width: `${safePercent}%` }}
                                aria-valuenow={safePercent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            />
                        </div>
                        <span style={{ minWidth: '48px' }}>{`${percent}%`}</span>
                    </div>
                )
            },
        },
        {
            Header: 'Working Tier',
            accessor: 'workingTier',
            Cell: (tableData) => {
                const row = tableData.row.original
                const wt = row.workingTier
                return <>{wt && wt.name ? wt.name : '-'}</>
            },
        },
        {
            Header: 'Completed Tier',
            accessor: 'completedTier',
            Cell: (tableData) => {
                const row = tableData.row.original
                const ct = row.completedTier
                return <>{ct && ct.name ? ct.name : '-'}</>
            },
        },              
        {
            Header: 'Promo Start',
            accessor: 'promoStartDate',
            Cell: (tableData) => {
                const row = tableData.row.original
                return <>{row.promoStartDate}</>
            },
        },
        {
            Header: 'Promo End',
            accessor: 'promoEndDate',
            Cell: (tableData) => {
                const row = tableData.row.original
                return <>{row.promoEndDate}</>
            },
        },
        {
            Header: 'Remaining Days',
            accessor: 'remainingDays',
            className: 'text-center',
            headerClassName: 'text-left',
            Cell: (tableData) => {
                const row = tableData.row.original
                const remainingDays = calculateRemainingDays(row.promoEndDate)
                
                if (remainingDays === null) {
                    return <>—</>
                }
                
                return <span>{remainingDays}</span>
            },
        },
    ]

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Affiliate Performance Bonus Report</h1>
            </div>
            <div className="row">
                <div className="col-lg-8">
                    <div className="card">                    
                            <div className="d-flex align-items-center p-3">
                                <DateRangeFilter
                                    quickDate={quickDate}
                                    selectedStartDate={selectedStartDate}
                                    selectedEndDate={selectedEndDate}
                                    handleChangeStart={handleChangeStart}
                                    handleChangeEnd={handleChangeEnd}
                                    handleQuickDate={handleQuickDate}
                                />
                                <button
                                    onClick={refreshReport}
                                    className="btn btn-sm btn-primary ml-2"
                                >
                                    Refresh Report
                                </button>
                            </div>

                            {dataLoaded ? (
                                report.length === 0 ? (
                                    <div className="p-3">No stats for the current parameters.</div>
                                ) : (
                                    <ReportTable data={report} columns={columns} />
                                )
                            ) : (
                                <Loading />
                            )}
                        
                    </div>
                </div> 
            </div>           
        </div>
    )
}

export default AffiliateBonusReport
